// TileFan — 増えうる PILE (花牌/北抜き/必要なら河) を lossless に重ね描画する扇。
// 全牌が DOM に残り順序・identity を保つ。下限 tileSize でも溢れる時 step を圧縮 (負 margin) し
// 空間を有界化、密な時のみ additive ×N を併記 (牌の代替ではない)。なぜ margin-left 負か:
// transform は TileView の data-aka リボン等と競合しないが、レイアウト box を動かさないため
// 重なりに margin を使う (隣の占有幅を step に圧縮できる)。クラスは theme.css の .tile-fan* を参照。
import { type CSSProperties } from 'react';
import type { TileId } from '@mj/protocol';
import type { AtlasManifest } from '@mj/assets';
import { TileView } from './TileView';
import { fitRow } from '../ui/fit';

// 他家手牌プロキシ用の裏牌 (情報量ゼロ — 枚数のみ ×N で保持)。MeldView の FaceDownTile と同じ意匠を
// 扇 (TileFan) でも再現するための小描画。faceDown 扇の各牌はこれで置換する (表牌の TileView を出さない)。
function FaceDownBack({ size }: { size: number }) {
  const h = Math.round(size * 1.36);
  const style: CSSProperties = {
    display: 'inline-block',
    width: size,
    height: h,
    borderRadius: Math.max(2, Math.round(size * 0.11)),
    background: 'linear-gradient(180deg,#1f6b46,#15513a)',
    border: '1px solid #0d3829',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.07)',
  };
  return <span style={style} title="裏" />;
}

export interface TileFanProps {
  tiles: TileId[]; // 描画する全牌 (識別子保持; key にも使う)
  boxW: number; // 利用可能幅(px)。useElementSize で測った親幅を渡す
  tileSize: number; // 自然牌幅(px)。fitRow の natW
  minW?: number; // 既定 10 (扇下限)
  gap?: number; // 既定 1 (重ならない時の牌間)
  title?: string; // ホバー (例 "北抜き ×N")
  badgeLabel?: (n: number) => string; // 既定 (n)=>`×${n}`
  className?: string;
  // 裏牌として扇る (他家手牌プロキシ)。各牌を FaceDownBack で描き、表は出さない。識別子は key/枚数にのみ使う。
  faceDown?: boolean;
  // 描画一貫性のための任意パススルー (TileView 既定と同じ; 未指定なら placeholder/SVG 経路)。
  manifest?: AtlasManifest | null;
  atlasBaseUrl?: string;
}

const defaultBadge = (n: number) => `×${n}`;

export function TileFan({
  tiles,
  boxW,
  tileSize,
  minW = 10,
  gap = 1,
  title,
  badgeLabel,
  className,
  faceDown = false,
  manifest,
  atlasBaseUrl,
}: TileFanProps) {
  if (tiles.length === 0) return null;

  // ① natural/shrunk で収まればそのまま、② 溢れる時のみ fanned (step 圧縮)、③ 密なら badge 併記。
  const f = fitRow({ containerW: boxW, count: tiles.length, natW: tileSize, gap, minW });
  const tileW = Math.max(minW, Math.round(f.tileW));
  // 重なり量: natural/shrunk では step-tileW = gap (>=0)、fanned では負 (隣に食い込む)。
  const overlap = f.step - tileW;
  const rootClass = className ? `tile-fan ${className}` : 'tile-fan';
  const rootStyle: CSSProperties = boxW > 0 ? { maxWidth: boxW } : {};
  const label = (badgeLabel ?? defaultBadge)(tiles.length);

  return (
    <span className={rootClass} style={rootStyle} title={title}>
      {tiles.map((t, i) => {
        // i===0 は通常配置、i>0 は overlap を margin-left に注入 (zIndex は後の牌ほど上で順序が視認できる)。
        const itemStyle: CSSProperties = { zIndex: i };
        if (i > 0) itemStyle.marginLeft = overlap;
        return (
          <span className="tile-fan-item" style={itemStyle} key={`${t}:${i}`}>
            {faceDown ? (
              <FaceDownBack size={tileW} />
            ) : (
              <TileView
                tile={t}
                size={tileW}
                {...(manifest !== undefined ? { manifest } : {})}
                {...(atlasBaseUrl !== undefined ? { atlasBaseUrl } : {})}
              />
            )}
          </span>
        );
      })}
      {/* additive ×N: 牌は全部描いた上で「補助」として併記 (牌の代替ではない) */}
      {f.badge && <span className="tile-fan-badge">{label}</span>}
    </span>
  );
}
